import { useState, useEffect } from "react";
import { usersApi, salaryApi, resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Users, UserCheck, UserX, Phone, Mail, ChevronRight, KeyRound, Trash2, Eye, EyeOff, Receipt, CheckCircle, Download } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const Employees = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [specOpen, setSpecOpen] = useState(false);
  const [specData, setSpecData] = useState(null);
  const [specLoading, setSpecLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "anställd",
    phone: "",
    title: "",
    profile_image: "",
    hourly_cost: "",
    monitor_time: true,
    hourly_wage: "",
    tax_rate: "30",
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await usersApi.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error("Kunde inte ladda användare");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updates = { 
          name: formData.name, 
          role: formData.role,
          phone: formData.phone,
          title: formData.title,
          profile_image: formData.profile_image,
          hourly_cost: parseFloat(formData.hourly_cost) || 0,
          monitor_time: formData.monitor_time,
          hourly_wage: parseFloat(formData.hourly_wage) || 0,
          tax_rate: parseFloat(formData.tax_rate) || 30,
          show_invoice_spec: formData.show_invoice_spec,
          company: formData.company,
          company_email: formData.company_email,
        };
        await usersApi.update(editingUser.id, updates);
        toast.success("Användare uppdaterad");
      } else {
        await usersApi.create(formData);
        toast.success("Användare skapad");
      }
      setDialogOpen(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte spara");
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role,
      phone: user.phone || "",
      title: user.title || "",
      profile_image: user.profile_image || "",
      hourly_cost: user.hourly_cost?.toString() || "",
      monitor_time: user.monitor_time !== false,
      hourly_wage: user.hourly_wage?.toString() || "",
      tax_rate: user.tax_rate?.toString() || "30",
      show_invoice_spec: user.show_invoice_spec || false,
      company: user.company || "",
      company_email: user.company_email || "",
    });
    setDialogOpen(true);
  };

  const handleToggleActive = async (user) => {
    try {
      await usersApi.update(user.id, { is_active: !user.is_active });
      toast.success(user.is_active ? "Inaktiverad" : "Aktiverad");
      loadUsers();
    } catch (error) {
      toast.error("Kunde inte uppdatera");
    }
  };

  const showUserDetails = (user) => {
    setSelectedUser(user);
    setDetailsOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Lösenord måste vara minst 6 tecken");
      return;
    }
    try {
      await usersApi.resetPassword(selectedUser.id, newPassword);
      toast.success("Lösenord återställt");
      setPasswordOpen(false);
      setNewPassword("");
    } catch (error) {
      toast.error("Kunde inte återställa lösenord");
    }
  };

  const handleDeleteUser = async (user, permanent = false) => {
    const confirmMsg = permanent 
      ? `VARNING: Ta bort ${user.name} PERMANENT? All data (tidrapporter, scheman) försvinner.`
      : `Inaktivera ${user.name}?`;
    
    if (!confirm(confirmMsg)) return;
    
    try {
      await usersApi.delete(user.id, permanent);
      toast.success(permanent ? "Användare borttagen" : "Användare inaktiverad");
      setDetailsOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte ta bort");
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({ name: "", email: "", password: "", role: "anställd", phone: "", title: "", profile_image: "", hourly_cost: "", monitor_time: true, hourly_wage: "", tax_rate: "30", show_invoice_spec: false, company: "", company_email: "" });
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      projektledare: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      anställd: "bg-gray-500/20 text-white/40 border-gray-500/30",
      underentreprenör: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    };
    const labels = { underentreprenör: "UE" };
    return <Badge className={styles[role] || styles.anställd} variant="outline">{labels[role] || role}</Badge>;
  };

  const getInitials = (name) => name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]"></div></div>;
  }

  return (
    <div className="space-y-6 slide-up" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-5 h-5 text-[#6dbf4a]" />
              <h1 className="text-2xl font-bold tracking-tight text-white">Anställda</h1>
            </div>
            <p className="text-white/25 text-sm">Hantera personal och roller</p>
          </div>
        </div>
        {isAdmin && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="gap-2 bg-[#6dbf4a] hover:bg-[#5da83d]">
              <Plus className="w-4 h-4" />Ny anställd
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] bg-[#0d1218] border-white/[0.06] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-white">{editingUser ? "Redigera" : "Ny anställd"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 pr-1" style={{ maxHeight: 'calc(90vh - 120px)' }}>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/40">Namn *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-[#080c12] border-white/[0.06] text-white" required />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/40">Titel</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="T.ex. Snickare" className="bg-[#080c12] border-white/[0.06] text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/40">E-post *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-[#080c12] border-white/[0.06] text-white" required disabled={!!editingUser} />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/40">Telefon</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="070-123 45 67" className="bg-[#080c12] border-white/[0.06] text-white" />
                </div>
              </div>
              {!editingUser && (
                <div className="space-y-2">
                  <Label className="text-white/40">Lösenord *</Label>
                  <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="bg-[#080c12] border-white/[0.06] text-white" required />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/40">Roll</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                    <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="projektledare">Projektledare</SelectItem>
                      <SelectItem value="anställd">Anställd</SelectItem>
                      <SelectItem value="underentreprenör">Underentreprenör (UE)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/40">Bild-URL</Label>
                  <Input value={formData.profile_image} onChange={(e) => setFormData({ ...formData, profile_image: e.target.value })} placeholder="https://..." className="bg-[#080c12] border-white/[0.06] text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-white/40">Lönekostnad (kr/h)</Label>
                <Input type="number" min="0" value={formData.hourly_cost} onChange={(e) => setFormData({ ...formData, hourly_cost: e.target.value })}
                  placeholder="T.ex. 250" className="bg-[#080c12] border-white/[0.06] text-white" data-testid="employee-hourly-cost" />
                <p className="text-xs text-white/40">Vad du betalar per timme (visas inte för anställda)</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-white/40">Timlön (kr/h)</Label>
                  <Input type="number" min="0" value={formData.hourly_wage} onChange={(e) => setFormData({ ...formData, hourly_wage: e.target.value })}
                    placeholder="T.ex. 180" className="bg-[#080c12] border-white/[0.06] text-white" data-testid="employee-hourly-wage" />
                  <p className="text-xs text-white/25">Anställds timlön (visas i lönespec)</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/40">Skattesats (%)</Label>
                  <Input type="number" min="0" max="100" value={formData.tax_rate} onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                    placeholder="30" className="bg-[#080c12] border-white/[0.06] text-white" data-testid="employee-tax-rate" />
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#080c12] border border-white/[0.06] rounded-xl">
                <div>
                  <Label className="text-white text-sm">Övervaka tidrapportering</Label>
                  <p className="text-xs text-white/25 mt-0.5">Visa i "Saknade tidsrapporter" på Dashboard</p>
                </div>
                <button
                  type="button"
                  data-testid="employee-monitor-toggle"
                  onClick={() => setFormData({ ...formData, monitor_time: !formData.monitor_time })}
                  className={`relative w-11 h-6 rounded-xl transition-colors duration-150 ${formData.monitor_time ? 'bg-[#6dbf4a]' : 'bg-zinc-700'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-xl transition-transform duration-150 ${formData.monitor_time ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              {formData.role === "underentreprenör" && (
              <>
              <div className="space-y-2">
                <Label className="text-white/40">Företag</Label>
                <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="T.ex. Hamravent AB" className="bg-[#080c12] border-white/[0.06] text-white" data-testid="employee-company" />
                <p className="text-xs text-white/25">UE:s företagsnamn (för grupperade fakturaunderlag)</p>
              </div>
              <div className="space-y-2">
                <Label className="text-white/40">Företagets e-post</Label>
                <Input type="email" value={formData.company_email} onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                  placeholder="chef@hamravent.se" className="bg-[#080c12] border-white/[0.06] text-white" data-testid="employee-company-email" />
                <p className="text-xs text-white/25">Fakturaunderlag skickas hit</p>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#080c12] border border-white/[0.06] rounded-xl">
                <div>
                  <Label className="text-white text-sm">Visa fakturaunderlag</Label>
                  <p className="text-xs text-white/25 mt-0.5">UE ser sitt fakturaunderlag på Dashboard</p>
                </div>
                <button
                  type="button"
                  data-testid="employee-invoice-spec-toggle"
                  onClick={() => setFormData({ ...formData, show_invoice_spec: !formData.show_invoice_spec })}
                  className={`relative w-11 h-6 rounded-xl transition-colors duration-150 ${formData.show_invoice_spec ? 'bg-[#6dbf4a]' : 'bg-zinc-700'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-xl transition-transform duration-150 ${formData.show_invoice_spec ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              </>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/[0.06] text-white/40">Avbryt</Button>
                <Button type="submit" className="bg-[#6dbf4a] hover:bg-[#5da83d]">{editingUser ? "Spara" : "Skapa"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {users.map((user) => {
          const roleAccentColors = {
            admin: "#a855f7",
            projektledare: "#3b82f6",
            anställd: "#6dbf4a",
            underentreprenör: "#f97316",
          };
          const accentColor = roleAccentColors[user.role] || "#6dbf4a";
          
          return (
            <Card key={user.id}
              className={`bg-[#0d1218] border-white/[0.06] cursor-pointer transition-all group hover:border-white/[0.12] ${!user.is_active ? "opacity-50" : ""}`}
              onClick={() => showUserDetails(user)}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <Avatar className="w-14 h-14 border-2 border-white/[0.06]">
                    <AvatarImage src={resolveImageUrl(user.profile_image)} />
                    <AvatarFallback style={{ backgroundColor: `${accentColor}15`, color: accentColor }} className="text-lg font-bold">{getInitials(user.name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-white truncate">{user.name}</h3>
                      <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-[#6dbf4a] flex-shrink-0" />
                    </div>
                    {user.title && <p className="text-sm text-white/40">{user.title}</p>}
                    <div className="mt-2 flex items-center gap-2">
                      {getRoleBadge(user.role)}
                      {isAdmin && user.hourly_cost > 0 && (
                        <span className="text-xs text-white/40">{user.hourly_cost} kr/h</span>
                      )}
                      {!user.is_active && <Badge variant="outline" className="text-red-400 border-red-500/30 text-xs">Inaktiv</Badge>}
                      {isAdmin && user.monitor_time === false && (
                        <span className="text-xs text-white/25 flex items-center gap-1" title="Övervakas ej"><EyeOff className="w-3 h-3" /></span>
                      )}
                    </div>
                  </div>
                </div>
                {(user.phone || user.email) && (
                  <div className="mt-3 pt-3 border-t border-white/[0.06] space-y-1">
                    {user.phone && (
                      <div className="flex items-center gap-2 text-sm text-white/40">
                        <Phone className="w-3 h-3 text-[#6dbf4a]" />{user.phone}
                      </div>
                    )}
                    {user.email && (
                      <div className="flex items-center gap-2 text-sm text-white/40 truncate">
                        <Mail className="w-3 h-3 text-[#6dbf4a]" />{user.email}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* User Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-md bg-[#0d1218] border-white/[0.06]">
          {selectedUser && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16 border-2 border-[#6dbf4a]/30">
                    <AvatarImage src={resolveImageUrl(selectedUser.profile_image)} />
                    <AvatarFallback className="bg-[#6dbf4a]/20 text-[#6dbf4a] text-xl">{getInitials(selectedUser.name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <DialogTitle className="text-xl text-white">{selectedUser.name}</DialogTitle>
                    {selectedUser.title && <p className="text-white/40">{selectedUser.title}</p>}
                    <div className="mt-1 flex items-center gap-2">
                      {getRoleBadge(selectedUser.role)}
                      {isAdmin && selectedUser.hourly_cost > 0 && (
                        <span className="text-sm text-white/40">{selectedUser.hourly_cost} kr/h</span>
                      )}
                    </div>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {selectedUser.phone && (
                  <a href={`tel:${selectedUser.phone}`} className="flex items-center gap-3 p-3 bg-[#080c12] rounded-xl hover:bg-[#0a0e14] transition-colors">
                    <div className="w-10 h-10 rounded-full bg-[#6dbf4a]/20 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-[#6dbf4a]" />
                    </div>
                    <div>
                      <p className="text-sm text-white/40">Telefon</p>
                      <p className="text-white font-medium">{selectedUser.phone}</p>
                    </div>
                  </a>
                )}
                
                <a href={`mailto:${selectedUser.email}`} className="flex items-center gap-3 p-3 bg-[#080c12] rounded-xl hover:bg-[#0a0e14] transition-colors">
                  <div className="w-10 h-10 rounded-full bg-[#6dbf4a]/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#6dbf4a]" />
                  </div>
                  <div>
                    <p className="text-sm text-white/40">E-post</p>
                    <p className="text-white font-medium">{selectedUser.email}</p>
                  </div>
                </a>

                {isAdmin && (
                <div className="flex items-center justify-between p-3 bg-[#080c12] rounded-xl border border-white/[0.06]">
                  <div>
                    <p className="text-sm text-white font-medium">Övervaka tidrapportering</p>
                    <p className="text-xs text-white/25">Visas i saknade tidsrapporter</p>
                  </div>
                  <button
                    data-testid="detail-monitor-toggle"
                    onClick={async () => {
                      const newVal = selectedUser.monitor_time === false ? true : false;
                      try {
                        await usersApi.update(selectedUser.id, { monitor_time: newVal });
                        setSelectedUser({ ...selectedUser, monitor_time: newVal });
                        loadUsers();
                        toast.success(newVal ? "Övervakning aktiverad" : "Övervakning avaktiverad");
                      } catch { toast.error("Kunde inte uppdatera"); }
                    }}
                    className={`relative w-11 h-6 rounded-xl transition-colors duration-150 ${selectedUser.monitor_time !== false ? 'bg-[#6dbf4a]' : 'bg-zinc-700'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-xl transition-transform duration-150 ${selectedUser.monitor_time !== false ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>
                )}

                {isAdmin && (
                <div className="flex gap-2 pt-4">
                  <Button onClick={() => { setDetailsOpen(false); handleEdit(selectedUser); }} className="flex-1 gap-2 bg-[#0d1218] border-white/[0.06] text-white hover:bg-[#3a3a3a]" variant="outline">
                    <Pencil className="w-4 h-4" />Redigera
                  </Button>
                  <Button onClick={async () => {
                    setSpecLoading(true);
                    try {
                      const res = await salaryApi.getUser(selectedUser.id);
                      setSpecData(res.data);
                      setSpecOpen(true);
                    } catch { toast.error("Kunde inte ladda underlag"); }
                    setSpecLoading(false);
                  }} className="flex-1 gap-2 bg-[#6dbf4a] hover:bg-[#5da83d] text-white" disabled={specLoading} data-testid="view-salary-spec-btn">
                    <Receipt className="w-4 h-4" />{selectedUser.role === 'underentreprenör' ? 'Fakturaunderlag' : 'Lönespec'}
                  </Button>
                </div>
                )}
                {isAdmin && (
                <div className="flex gap-2 mt-2">
                  <Button onClick={() => { setPasswordOpen(true); }} className="flex-1 gap-2 bg-amber-600 hover:bg-amber-700 text-white">
                    <KeyRound className="w-4 h-4" />Lösenord
                  </Button>
                </div>
                )}
                {isAdmin && (
                <div className="flex gap-2 mt-2">
                  <Button onClick={() => { handleToggleActive(selectedUser); setDetailsOpen(false); }} className={`flex-1 gap-2 ${selectedUser.is_active ? 'bg-[#0d1218] border-white/[0.06] text-white hover:bg-[#3a3a3a]' : 'bg-[#6dbf4a] text-white hover:bg-[#5da83d]'}`} variant="outline">
                    {selectedUser.is_active ? <><UserX className="w-4 h-4" />Inaktivera</> : <><UserCheck className="w-4 h-4" />Aktivera</>}
                  </Button>
                  <Button onClick={() => handleDeleteUser(selectedUser, true)} variant="destructive" className="flex-1 gap-2">
                    <Trash2 className="w-4 h-4" />Ta bort
                  </Button>
                </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Reset Modal */}
      <Dialog open={passwordOpen} onOpenChange={setPasswordOpen}>
        <DialogContent className="max-w-sm bg-[#0d1218] border-white/[0.06]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-400" />
              Återställ lösenord
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-white/40 text-sm">Nytt lösenord för {selectedUser?.name}</p>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minst 6 tecken"
              className="bg-[#080c12] border-white/[0.06] text-white"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPasswordOpen(false)} className="flex-1 border-white/[0.06] text-white/40">Avbryt</Button>
              <Button onClick={handleResetPassword} className="flex-1 bg-amber-600 hover:bg-amber-700">Återställ</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Salary Spec Modal */}
      <Dialog open={specOpen} onOpenChange={setSpecOpen}>
        <DialogContent className="max-w-lg bg-[#0d1218] border-white/[0.06]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#6dbf4a]" />
              {specData?.is_ue ? "Fakturaunderlag" : "Lönespec"} — {specData?.user_name}
            </DialogTitle>
          </DialogHeader>
          {specData && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-white/25">{specData.period_label}</span>
                <div className="flex items-center gap-2">
                  {specData.approved ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-lg bg-emerald-500/15 text-emerald-400 font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" />Godkänd</span>
                  ) : (
                    <Button size="sm" onClick={async () => {
                      try {
                        await salaryApi.approve(specData.user_id, specData.period);
                        setSpecData({ ...specData, approved: true });
                        toast.success("Godkänd!");
                      } catch { toast.error("Kunde inte godkänna"); }
                    }} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="approve-spec-btn">
                      Godkänn
                    </Button>
                  )}
                  <a
                    href={`${salaryApi.getPdfUrl(specData.user_id, specData.period)}&token=${localStorage.getItem('token')}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[#6dbf4a]/10 text-[#6dbf4a] rounded-lg hover:bg-[#6dbf4a]/20 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" /> PDF
                  </a>
                </div>
              </div>

              {specData.is_ue ? (
                <>
                  <div className="flex justify-between py-2 border-b border-white/[0.04]">
                    <span className="text-sm text-white/40">Timtakst</span>
                    <span className="stat-number text-sm text-white">{specData.hourly_rate} kr/h</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/[0.04]">
                    <span className="text-sm text-white/40">Timmar</span>
                    <span className="stat-number text-sm text-white">{specData.total_hours?.toFixed(1)} h</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/[0.04]">
                    <span className="text-sm text-white/40">Summa arbete</span>
                    <span className="stat-number text-sm text-white">{specData.total?.toLocaleString("sv-SE")} kr</span>
                  </div>
                  {specData.total_mileage > 0 && (
                    <div className="flex justify-between py-2 border-b border-white/[0.04]">
                      <span className="text-sm text-white/40">Milersättning ({specData.total_mileage} mil)</span>
                      <span className="stat-number text-sm text-[#6dbf4a]">+{specData.mileage_pay?.toLocaleString("sv-SE")} kr</span>
                    </div>
                  )}
                  <div className="flex justify-between py-3 rounded-xl px-3" style={{ background: 'rgba(109,191,74,0.08)', border: '1px solid rgba(109,191,74,0.15)' }}>
                    <span className="text-sm font-semibold text-white">Att fakturera</span>
                    <span className="stat-number text-lg font-bold text-[#6dbf4a]">{specData.grand_total?.toLocaleString("sv-SE")} kr</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between py-2 border-b border-white/[0.04]">
                    <span className="text-sm text-white/40">Timlön × {specData.total_hours?.toFixed(1)}h</span>
                    <span className="stat-number text-sm text-white">{specData.gross?.toLocaleString("sv-SE")} kr</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/[0.04]">
                    <span className="text-sm text-white/40">Semesterersättning (12%)</span>
                    <span className="stat-number text-sm text-[#6dbf4a]">+{specData.vacation_pay?.toLocaleString("sv-SE")} kr</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/[0.04]">
                    <span className="text-sm text-white/40">Milersättning ({specData.total_mileage} mil)</span>
                    <span className="stat-number text-sm text-[#6dbf4a]">+{specData.mileage_pay?.toLocaleString("sv-SE")} kr</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/[0.04]">
                    <span className="text-sm text-white/40">Skatteavdrag ({specData.tax_rate}%)</span>
                    <span className="stat-number text-sm text-red-400">−{specData.tax?.toLocaleString("sv-SE")} kr</span>
                  </div>
                  <div className="flex justify-between py-3 rounded-xl px-3" style={{ background: 'rgba(109,191,74,0.08)', border: '1px solid rgba(109,191,74,0.15)' }}>
                    <span className="text-sm font-semibold text-white">Nettolön</span>
                    <span className="stat-number text-lg font-bold text-[#6dbf4a]">{specData.net?.toLocaleString("sv-SE")} kr</span>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;
